import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { revisionSchema } from '../validators'
import { ALERT_SEVERITIES, FEED_PROVIDERS, vocab } from '../vocab'

/**
 * Operational alerts (P-FEED-005): feed failures, late deliveries, partial
 * runs, high-severity signals on held issuers, audit and job failures.
 * Shown in the admin panel; failed and late runs are also emailed to
 * OPS_ALERT_EMAIL, at most once per provider per 24 hours. Since O-06, API
 * 5xx responses raise `api_error`, and job and audit-verification failures
 * are emailed too (at most once per job or chain per 24 hours).
 */
export const OPS_ALERT_KINDS = vocab(
  [
    'feed_failed',
    'feed_late',
    'feed_partial',
    'high_severity_signal',
    'feed_health',
    'audit_verify_failed',
    'audit_verify_ok',
    'job_failed',
    // appended (wave 3, O-06)
    'api_error',
    'platform_health',
  ],
  {
    feed_failed: 'Feed run failed',
    feed_late: 'Feed delivery late',
    feed_partial: 'Feed run partial',
    high_severity_signal: 'High-severity signal on a held issuer',
    feed_health: 'Feed health summary',
    audit_verify_failed: 'Audit chain verification failed',
    audit_verify_ok: 'Audit chains verified',
    job_failed: 'Scheduled job failed',
    api_error: 'API server error (5xx)',
    platform_health: 'Platform health summary',
  },
)
export type OpsAlertKind = (typeof OPS_ALERT_KINDS.values)[number]

export const ALERT_EMAIL_KINDS: readonly string[] = ['feed_failed', 'feed_late']
export const ALERT_EMAIL_WINDOW_HOURS = 24

export const alertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  kind_label: z.string(),
  severity: z.enum(ALERT_SEVERITIES.values),
  subject: z.string(),
  body: z.string(),
  provider_id: z.string().nullable(),
  provider_name: z.string().nullable(),
  run_id: z.string().nullable(),
  signal_id: z.string().nullable(),
  issuer_id: z.string().nullable(),
  org_count: z.number().int(),
  created_at: z.string(),
  acknowledged_by: z.string().nullable(),
  acknowledged_by_name: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  emailed_at: z.string().nullable(),
  revision: z.number().int(),
})
export type Alert = z.infer<typeof alertSchema>

export const ALERT_STATUS_FILTERS = ['open', 'acknowledged', 'all'] as const

export const alertsQuerySchema = pageQuerySchema.extend({
  status: z.enum(ALERT_STATUS_FILTERS).default('open'),
  severity: z.enum(ALERT_SEVERITIES.values).optional(),
  kind: z.enum(OPS_ALERT_KINDS.values).optional(),
  provider: z.enum(FEED_PROVIDERS.values).optional(),
})
export type AlertsQuery = z.infer<typeof alertsQuerySchema>

export const alertAcknowledgeSchema = z.strictObject({ revision: revisionSchema })
export type AlertAcknowledge = z.infer<typeof alertAcknowledgeSchema>

export const alertCountsSchema = z.object({
  open: z.number().int(),
  critical: z.number().int(),
  warning: z.number().int(),
  info: z.number().int(),
})
export type AlertCounts = z.infer<typeof alertCountsSchema>

export const alertPageSchema = z.object({
  items: z.array(alertSchema),
  next_cursor: z.string().nullable(),
  counts: alertCountsSchema,
})
export type AlertPage = z.infer<typeof alertPageSchema>
